import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Form, Input, Select, Button, Typography, Space, Upload, Row, Col, Badge, InputNumber } from "antd";
import { SaveOutlined, ReloadOutlined, ArrowLeftOutlined, UploadOutlined, TranslationOutlined, SettingOutlined, EyeOutlined, SortAscendingOutlined, PictureOutlined } from "@ant-design/icons";
import videoService from "../../../services/videoService";
import masterService from "../../../services/masterService";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";
import { getImageUrl } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Option } = Select;

const AddLanguage = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const [imageUrl, setImageUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const langKeywords = Array.isArray(values.lang_keywords)
        ? values.lang_keywords
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(",")
        : values.lang_keywords || "";
      const payload = { ...values, lang_keywords: langKeywords };
      const response = await videoService.addVideoLanguage(payload);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Language added successfully",
        });
        form.resetFields();
        navigate("/languages");
      } else {
        notify.error({
          message: response.data.message || "Failed to add language",
        });
      }
    } catch (error) {
      console.error("Error adding language:", error);
      notify.error({ message: "Error adding language" });
    } finally {
      setLoading(false);
    }
  };

  const onImageUpload = async (file, key) => {
    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "language");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      console.log(
        "response.data.data.s3_key",
        response.data.data.s3_key
      );
      const uploadedUrl = response.data.data.s3_key;
      form.setFieldValue(key, uploadedUrl);

      // Update state variables to trigger re-render
      if (key === "image_url") {
        setImageUrl(uploadedUrl);
      } else if (key === "logo_url") {
        setLogoUrl(uploadedUrl);
      }

      notify.success({ message: "Image uploaded successfully" });
    } catch (e) {
      notify.error({ message: "Failed to upload image" });
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/languages");
  };

  useEffect(() => {
    setPageTitle("Add Language");
  }, [setPageTitle]);

  return (
    <div className="min-h-screen">
      <div className="">
        {/* Header Section */}

        <Card
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
            initialValues={{
              lang_is_active: "Y",
            }}
            className="space-y-6"
          >
            {/* Basic Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <TranslationOutlined  />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Basic Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Language Name 
                      </span>
                    }
                    name="lang_name"
                    rules={[
                      { required: true, message: "Please enter language name" },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter language name"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Language ISO Code
                      </span>
                    }
                    name="lang_iso_639_2_code"
                  >
                    <Input
                      placeholder="Enter language ISO code"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Native Name
                      </span>
                    }
                    name="native_name"
                  >
                    <Input
                      placeholder="Enter native name"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Description
                      </span>
                    }
                    name="lang_description"
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder="Enter description"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Keywords
                      </span>
                    }
                    name="lang_keywords"
                  >
                    <Select
                      mode="tags"
                      placeholder="Type a keyword and press Enter"
                      tokenSeparators={[","]}
                      open={false}
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Status & Translation Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <SettingOutlined  />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Status & Translation
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="lang_is_active"
                  >
                    <Select placeholder="Select status" className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Allow Translation
                      </span>
                    }
                    name="is_translation_active"
                  >
                    <Select
                      placeholder="Select translation option"
                      className="rounded-lg"
                    >
                      <Option value="Y">
                        <Badge status="success" text="Yes" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="No" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Preference Settings Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <EyeOutlined  />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Preference Settings
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Show in Preference
                      </span>
                    }
                    name="show_in_preference"
                  >
                    <Select
                      placeholder="Select preference visibility"
                      className="rounded-lg"
                    >
                      <Option value="Y">
                        <Badge status="success" text="Yes" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="No" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Preference Order
                      </span>
                    }
                    name="preference_order_number"
                    rules={[
                      {
                        pattern: /^(0|[1-9]\d*)$/,
                        message:
                          "Preference order number must be a non-negative integer",
                      },
                    ]}
                  >
                    <InputNumber
                      placeholder="Enter preference order number"
                      style={{ width: "100%" }}
                      className="rounded-lg"
                      addonBefore={<SortAscendingOutlined />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Media Assets Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <PictureOutlined  />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Media Assets
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="image_url"
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Language Image
                      </span>
                    }
                  >
                    {imageUrl && (
                      <div className="mb-4">
                        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                          <Text strong className="text-orange-700 text-sm">
                            {getImageUrl(imageUrl)}
                          </Text>
                        </div>
                        <div className="w-full h-32 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                          <img
                            src={getImageUrl(imageUrl)}
                            alt="Language preview"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      </div>
                    )}

                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(f) => {
                        onImageUpload(f, "image_url");
                        return false;
                      }}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        className="w-full rounded-lg"
                        type={imageUrl ? "default" : "primary"}
                      >
                        {imageUrl
                          ? "Change Language Image"
                          : "Upload Language Image"}
                      </Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="logo_url"
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Language Logo
                      </span>
                    }
                  >
                    {logoUrl && (
                      <div className="mb-4">
                        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                          <Text strong className="text-orange-700 text-sm">
                            {getImageUrl(logoUrl)}
                          </Text>
                        </div>
                        <div className="w-full h-32 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                          <img
                            src={getImageUrl(logoUrl)}
                            alt="Logo preview"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      </div>
                    )}

                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(f) => {
                        onImageUpload(f, "logo_url");
                        return false;
                      }}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        className="w-full rounded-lg"
                        type={logoUrl ? "default" : "primary"}
                      >
                        {logoUrl
                          ? "Change Language Logo"
                          : "Upload Language Logo"}
                      </Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Adding..." : "Add Language"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default AddLanguage;
